import type { ProgrammaticLandingPageConfig } from '../../types'
import {
  STORAGE_REFERENCE_KWP,
  SELF_CONSUMPTION_WITHOUT_BATTERY,
  SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_WITH_BATTERY,
  SELF_CONSUMPTION_CAP,
  BATTERY_COST_PER_KWH_RANGE_EUR,
} from '../storageSizeFacts'

export const KWH_20_BATERIA_SOLAR: ProgrammaticLandingPageConfig = {
  slug: 'bateria-solar-20-kwh-precio',
  lang: 'es',
  topicId: 'kwh-speicher-20',
  title: 'Batería solar de 20 kWh: precio y valores orientativos 2026',
  metaDescription:
    '¿Cuánto cuesta una batería solar de 20 kWh? Para quién es adecuada, valores orientativos de ' +
    'autoconsumo y comparación gratuita de presupuestos para instalación + batería.',
  keywords: [
    'batería solar 20 kWh precio',
    'acumulador solar 20 kWh coste',
    'batería fotovoltaica 20 kWh',
    'cuánto cuesta una batería de 20 kWh',
  ],
  eyebrow: 'Tamaño de batería: 20 kWh',
  h1: 'Batería solar de 20 kWh: precio y valores orientativos',
  intro:
    'Una batería de 20 kWh es nuestro tamaño de acumulador indexado más grande - pensada para ' +
    `hogares grandes con bomba de calor y coche eléctrico a la vez, combinada con una instalación ` +
    `de referencia de ${STORAGE_REFERENCE_KWP} kWp. Aquí encontrará valores orientativos sobre el ` +
    'tamaño de hogar adecuado, el autoconsumo con batería y justo debajo una calculadora con el ' +
    'precio medio ya calculado para esta combinación a partir de presupuestos reales de empresas ' +
    'instaladoras.',
  calculatorPrefill: { kind: 'kwp+storage', kwp: STORAGE_REFERENCE_KWP, kwh: 20 },
  indexPolicy: 'index',
  sections: [
    { type: 'heading', text: '¿Para quién es adecuada una batería de 20 kWh?' },
    {
      type: 'paragraph',
      text:
        `Valor orientativo: 20 kWh es adecuado para hogares grandes con bomba de calor Y coche ` +
        `eléctrico y un alto grado de autosuficiencia buscado. Con una instalación mayor que ` +
        `${STORAGE_REFERENCE_KWP} kWp, suele aumentar también la parte de batería que tiene ` +
        'sentido aprovechar - la calculadora de dimensionamiento ofrece una recomendación exacta ' +
        'para su tejado.',
    },
    { type: 'heading', text: 'Autoconsumo con batería solar' },
    {
      type: 'paragraph',
      text:
        `Sin batería, el autoconsumo de una instalación de ${STORAGE_REFERENCE_KWP} kWp ronda ` +
        `normalmente el ${SELF_CONSUMPTION_WITHOUT_BATTERY * 100} %. Con batería solar, este ` +
        `porcentaje sube en el modelo de cálculo unos ${SELF_CONSUMPTION_BATTERY_BONUS * 100} ` +
        `puntos hasta rondar el ${SELF_CONSUMPTION_WITH_BATTERY * 100} %, y con bomba de calor y ` +
        `coche eléctrico juntos hasta el límite del ${SELF_CONSUMPTION_CAP * 100} % - a partir de ` +
        'ahí, una batería aún mayor apenas aporta beneficio adicional según el modelo de cálculo. ' +
        'Un cálculo exacto según su consumo lo ofrece la calculadora de dimensionamiento.',
    },
    { type: 'heading', text: 'Suministro de emergencia' },
    {
      type: 'paragraph',
      text:
        'Con 20 kWh, una caja de respaldo para suministro de emergencia suele alcanzar para la ' +
        'mayor parte de un hogar durante un periodo más largo - el tiempo exacto depende del ' +
        'consumo real de los aparatos conectados. Si un presupuesto incluye o no esta función ' +
        'depende de cada instalador - conviene preguntarlo directamente.',
    },
    { type: 'heading', text: 'Coste de la batería y ayudas' },
    {
      type: 'paragraph',
      text:
        'El coste de las baterías varía mucho según el fabricante y la complejidad de la ' +
        'instalación - la investigación de mercado de 2026 muestra un rango de entre ' +
        `${BATTERY_COST_PER_KWH_RANGE_EUR.min} y ${BATTERY_COST_PER_KWH_RANGE_EUR.max.toLocaleString('es-ES')} ` +
        '€ por kWh, con un impacto claramente notable en el presupuesto total para 20 kWh, por lo ' +
        'que comparar precios merece especialmente la pena. En Alemania existen ayudas específicas ' +
        'para baterías según la región (p. ej. el programa del estado federado de Berlín, con ' +
        'subvención para baterías); en Austria, el sistema EAG añade 150 €/kWh para el acumulador ' +
        '- un cálculo exacto para su combinación lo ofrece la calculadora de abajo.',
    },
  ],
  faq: [
    {
      q: '¿Para quién merece la pena una batería de 20 kWh?',
      a: 'Sobre todo para hogares grandes con bomba de calor y coche eléctrico a la vez, con un alto grado de autosuficiencia buscado. Sin estos grandes consumidores, un tamaño menor suele ser más rentable.',
    },
    {
      q: '¿Aporta beneficios adicionales una batería mayor de 20 kWh?',
      a: 'Apenas, según el modelo de cálculo - el porcentaje de autoconsumo está limitado al 85 %, independientemente del tamaño de la batería. A partir de cierto punto, el coste crece más rápido que el beneficio adicional.',
    },
    {
      q: '¿Con qué tamaño de instalación se combina normalmente una batería de 20 kWh?',
      a: `Aquí se toma como referencia una instalación de ${STORAGE_REFERENCE_KWP} kWp; en la práctica también se combina a menudo con 20 kWp o más. La calculadora ofrece una recomendación exacta para su tejado.`,
    },
    {
      q: '¿Es suficiente 20 kWh para ser autosuficiente durante la noche?',
      a: 'Sí, esto suele bastar por completo incluso con consumo alto (por ejemplo carga de coche eléctrico y bomba de calor juntos). Una estimación exacta según su consumo real la ofrece la calculadora de dimensionamiento.',
    },
  ],
  primaryCta: { label: 'Solicitar presupuesto gratis ahora', action: 'customer' },
  secondaryCta: {
    label: 'Coste de una instalación fotovoltaica: guía completa',
    action: 'link',
    href: '/coste-instalacion-fotovoltaica',
  },
  relatedLinks: [
    { label: 'Batería solar de 15 kWh: precio y valores orientativos', href: '/bateria-solar-15-kwh-precio' },
    { label: 'Batería solar de 10 kWh: precio y valores orientativos', href: '/bateria-solar-10-kwh-precio' },
    { label: 'Instalación fotovoltaica de 20 kWp: precio y valores orientativos', href: '/instalacion-fotovoltaica-20-kwp-precio' },
    { label: 'Cómo funciona la comparación de presupuestos', href: '/comparar-presupuestos-fotovoltaicos' },
  ],
}
